import * as React from 'react';
import type { FieldPath, Path, PathValue, UseFormGetValues } from 'react-hook-form';
import { type FieldValues, type UseControllerProps } from 'react-hook-form';

import type { CellError } from '../../RHFTableCellComponents/error.types';
import { DisplayCell } from '../../RHFTableCellComponents/Input/DisplayCell';
import type { InputCellType, InputSharedProps } from '../../RHFTableCellComponents/Input/types';
import RHFUnregisteredFieldWrapper from '../RHFUnregisteredFieldWrapper';

type Props<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		type: InputCellType;
		rhfId: number | string;
		placeholder?: string;
		getValues: UseFormGetValues<TFieldValues>;
		readonly?: boolean;
		readonlyFieldClassName?: string;
		className?: string;
	} & InputSharedProps;

function Input<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, type, defaultValue, readonly, readonlyFieldClassName, prefixGlyphs, suffixGlyphs, className, ...rest } = props;

	const renderInput = React.useCallback((
		{ value, error }: {
			value: PathValue<TFieldValues, keyof TFieldValues & Path<TFieldValues>>;
			error: Nullable<CellError>;
		}) => (
		<DisplayCell
			className={`${readonlyFieldClassName ?? ''} ${className ?? ''}`}
			elementKey={`${rhfId}`}
			error={error ?? undefined}
			key={name + rhfId}
			name={name}
			placeholder={placeholder}
			prefixGlyphs={prefixGlyphs}
			readonly={readonly}
			suffixGlyphs={suffixGlyphs}
			type={type}
			value={value ?? defaultValue}
		/>
	),
		[readonlyFieldClassName, rhfId, name, placeholder, readonly, type, defaultValue, prefixGlyphs, suffixGlyphs, className]
	);

	return (
		<RHFUnregisteredFieldWrapper<TFieldValues, TName, TTransformedValues>
			name={name}
			{...rest}
			render={renderInput}
		/>
	);
}

export default React.memo(Input) as typeof Input;
